import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, Leaf } from "lucide-react";
import { AnimatedSection } from "./shared";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://www.google.com/maps?cid=13588561252063095004";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };
const stagger = (i) => ({ ...fadeUp, transition: { duration: 0.5, delay: i * 0.1 } });

const Stars = ({ count, size = 14 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={`star-${i}`} size={size}
        className={i < count ? "fill-[#FBBC04] text-[#FBBC04]" : "fill-gray-200 text-gray-200"} />
    ))}
  </div>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" className="flex-shrink-0">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function GoogleReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(4.9);
  const [reviewCount, setReviewCount] = useState(114);
  const [source, setSource] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/reviews/google`).then((res) => {
      if (cancelled) return;
      const d = res.data;
      setReviews(d.reviews || []);
      setRating(d.rating || 4.9);
      setReviewCount(d.review_count || 114);
      setSource(d.source || "curated");
    }).catch(() => {
      if (!cancelled) setSource("curated");
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch, API is a module constant

  const displayReviews = reviews.length > 0 ? reviews : [];

  return (
    <section data-testid="google-reviews-section" className="py-20 md:py-28 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #F5F9F6 0%, #FEFDFB 40%, #F5F9F6 100%)" }}>
      {/* Left side HD food border */}
      <div className="absolute left-0 top-0 bottom-0 w-[260px] hidden xl:block pointer-events-none">
        <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=600&h=1200&q=80" alt="" className="w-full h-full object-cover opacity-[0.10]" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#FAFCFA]" />
      </div>
      {/* Right side HD food border */}
      <div className="absolute right-0 top-0 bottom-0 w-[260px] hidden xl:block pointer-events-none">
        <img src="https://images.unsplash.com/photo-1490818387583-1baba5e638af?auto=format&fit=crop&w=600&h=1200&q=80" alt="" className="w-full h-full object-cover opacity-[0.10]" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#FAFCFA]" />
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <AnimatedSection>
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-3 bg-surface border border-border_custom px-5 py-2.5 rounded-full mb-6">
              <GoogleIcon />
              <span className="text-sm text-emerald_deep font-medium">Google Reviews</span>
              {source === "google_live" && (
                <span className="text-[9px] tracking-wider uppercase bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold" data-testid="live-badge">Live</span>
              )}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-emerald_deep mt-2">
              Trusted by <span className="text-accent" data-testid="review-count">{reviewCount}+</span> Clients
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4">
              <span className="font-serif text-5xl text-emerald_deep" data-testid="review-rating">{rating}</span>
              <div>
                <Stars count={Math.round(rating)} size={20} />
                <p className="text-xs text-text_muted mt-1">{reviewCount}+ reviews on Google</p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Review Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-testid="reviews-grid">
          {displayReviews.map((review, i) => (
            <motion.div key={review.name + i} {...stagger(i)} data-testid={`google-review-${i}`}
              className="bg-surface border border-border_custom rounded-2xl p-6 hover:border-accent/20 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {review.photo_uri ? (
                    <img src={review.photo_uri} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald_deep flex items-center justify-center">
                      <span className="text-bone font-serif text-sm">{review.name.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-emerald_deep">{review.name}</p>
                    <p className="text-xs text-text_muted">{review.time}</p>
                  </div>
                </div>
                <GoogleIcon />
              </div>
              <Stars count={review.rating} />
              <p className="text-sm text-text_muted leading-relaxed mt-3">{review.text}</p>
            </motion.div>
          ))}
        </div>

        {/* CTAs */}
        <motion.div {...fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer"
            data-testid="google-reviews-cta"
            className="inline-flex items-center gap-2 border border-border_custom text-emerald_deep px-8 py-3 text-sm tracking-wide hover:bg-emerald_deep hover:text-bone transition-all rounded-full">
            See All Reviews on Google <ExternalLink size={14} />
          </a>
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer"
            data-testid="write-review-cta"
            className="inline-flex items-center gap-2 bg-emerald_deep text-bone px-8 py-3 text-sm tracking-wide hover:bg-emerald_deep/90 transition-all rounded-full">
            <Star size={14} className="fill-accent text-accent" /> Write a Review
          </a>
        </motion.div>
      </div>
    </section>
  );
}
